import {
  ArrowLeft,
  Banknote,
  ChevronRight,
  Compass,
  MapPin,
  SlidersHorizontal,
  Star,
} from "lucide-react";
import { PhoneMock } from "../PhoneMock";

/**
 * App preview: the real Whistll "Browse Courts" (Book a Court) screen.
 *
 * Mirrors apps/mobile/src/pages/BrowseCourts.jsx: "Browse Courts" H1
 * with volt-coloured second word, volt pill filter row, and a 2-column
 * grid of venue cards with rating pill + "from £X/pp" pricing.
 */
export function BookACourtMock({ className }: { className?: string }) {
  return (
    <PhoneMock className={className}>
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 pt-4 pb-1 text-[10px] font-semibold">
        <span>9:41</span>
        <span className="flex items-center gap-1 text-[9px]">● ● ●</span>
      </div>

      {/* Back link */}
      <div className="px-4 pt-3 pb-1">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[#8899A6]">
          <ArrowLeft className="h-3 w-3" />
          Home
        </div>
      </div>

      {/* Title */}
      <div className="px-4 pb-2">
        <h1 className="text-[20px] font-black leading-tight tracking-tight text-[#F5F5DC]">
          Browse <span className="text-[#FF6200]">Courts</span>
        </h1>
        <p className="mt-0.5 text-[10px] text-[#8899A6]">
          Find and book courts near you
        </p>
      </div>

      {/* Pill filter row */}
      <div className="flex items-center gap-1.5 overflow-hidden px-4 pb-3">
        <SlidersHorizontal className="h-3 w-3 shrink-0 text-[#FF6200]" />
        <FilterPill icon={<MapPin className="h-2.5 w-2.5" />} label="London" />
        <FilterPill
          icon={<Compass className="h-2.5 w-2.5" />}
          label="10mi"
        />
        <FilterPill icon={<Star className="h-2.5 w-2.5" />} label="Rating" />
        <FilterPill
          icon={<Banknote className="h-2.5 w-2.5" />}
          label="Price"
        />
      </div>

      {/* Courts grid */}
      <div className="grid grid-cols-2 gap-2 px-4">
        <SmallVenueCardMock
          name="Brixton Rec"
          city="Brixton, London"
          rating={4.8}
          distance="1.2"
          price="£8.50"
        />
        <SmallVenueCardMock
          name="Westway"
          city="West London"
          rating={4.6}
          distance="2.4"
          price="£6.00"
        />
        <SmallVenueCardMock
          name="Copper Box"
          city="Stratford"
          rating={4.9}
          distance="3.8"
          price="£10.00"
        />
        <SmallVenueCardMock
          name="Oasis"
          city="Covent Garden"
          rating={4.5}
          distance="4.1"
          price="£7.50"
        />
      </div>
    </PhoneMock>
  );
}

function FilterPill({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <div className="flex shrink-0 items-center gap-1 rounded-full border border-[#FF6200] bg-[#FF6200] px-2 py-0.5 text-[9px] font-bold text-[#F5F5DC]">
      {icon}
      <span>{label}</span>
    </div>
  );
}

function SmallVenueCardMock({
  name,
  city,
  rating,
  distance,
  price,
}: {
  name: string;
  city: string;
  rating: number;
  distance: string;
  price: string;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#1e2832] bg-[#131a20]">
      {/* Image (gradient stand-in) */}
      <div
        className="relative aspect-[16/9] w-full"
        style={{
          backgroundImage:
            "linear-gradient(135deg, #1a2a3a 0%, #0d1117 40%, #1e2832 100%)",
        }}
      >
        <svg
          className="absolute inset-0 h-full w-full opacity-25"
          viewBox="0 0 100 60"
          preserveAspectRatio="none"
          aria-hidden
        >
          <rect
            x="10"
            y="10"
            width="80"
            height="40"
            fill="none"
            stroke="#FF8C42"
            strokeWidth="0.8"
          />
          <circle
            cx="50"
            cy="30"
            r="9"
            fill="none"
            stroke="#FF8C42"
            strokeWidth="0.8"
          />
        </svg>
        <div className="absolute right-1.5 top-1.5 flex items-center gap-0.5 rounded-full bg-black/70 px-1.5 py-0.5 text-[8px] font-bold text-[#FF6200]">
          <Star className="h-2 w-2 fill-current" />
          {rating.toFixed(1)}
        </div>
      </div>

      {/* Info */}
      <div className="p-2">
        <div className="flex items-start justify-between gap-1">
          <p className="truncate text-[10px] font-bold leading-tight text-[#F5F5DC]">
            {name}
          </p>
          <ChevronRight className="h-3 w-3 shrink-0 text-[#4a5568]" />
        </div>
        <div className="mt-1 flex items-center gap-1">
          <MapPin className="h-2 w-2 shrink-0 text-[#4a5568]" />
          <span className="truncate text-[8px] text-[#8899A6]">{city}</span>
          <Compass className="h-2 w-2 shrink-0 text-[#6496ff]" />
          <span className="text-[8px] font-semibold text-[#6496ff]">
            {distance}mi
          </span>
        </div>
        <div className="mt-1 flex justify-end">
          <div className="flex flex-col items-end leading-none">
            <span className="text-[7px] text-[#4a5568]">from</span>
            <span className="text-[9px] font-bold text-[#F5F5DC]">
              {price}/pp
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
